import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Une bulle d'information posée au survol d'un élément, garantie de tenir dans l'écran.
 *
 * ## Pourquoi un portail
 *
 * Les listes de la charte Kokpit ont `overflow: hidden` — indispensable pour que les coins
 * arrondis rognent la première et la dernière ligne. Un panneau posé dans une ligne serait donc
 * coupé dès la deuxième. La bulle sort par un portail, et sa position est calculée à l'entrée du
 * pointeur plutôt que dérivée d'un ancêtre qui n'existe plus.
 *
 * ## Pourquoi la position se décide après le rendu
 *
 * Elle se pose sous l'élément survolé, sauf quand elle n'y tient pas : sur la dernière ligne d'un
 * tableau, elle sortirait de l'écran et ses derniers éléments deviendraient illisibles. Or sa
 * hauteur dépend du contenu — un nom long occupe deux lignes — et l'estimer serait faux une fois
 * sur deux.
 *
 * On la mesure donc réellement, dans un `useLayoutEffect` : le navigateur ne peint pas entre la
 * mesure et la correction, il n'y a aucun saut visible. Tant que la mesure n'a pas eu lieu, la
 * bulle est rendue invisible plutôt qu'au mauvais endroit.
 *
 * @param {{x: number, bas: number, haut: number}} position  bords de l'élément survolé
 */
const MARGE = 12;

const BulleSurvol = ({ position, children }) => {
  const bulle = useRef(null);
  const [haut, setHaut] = useState(null);

  useLayoutEffect(() => {
    const element = bulle.current;
    if (!element) return;

    const hauteur = element.getBoundingClientRect().height;

    // Sous l'élément si elle y tient ; au-dessus sinon ; et si ni l'un ni l'autre — une bulle
    // plus haute que la fenêtre — collée en haut, son propre défilement fera le reste.
    if (position.bas + hauteur + MARGE <= window.innerHeight) setHaut(position.bas);
    else if (position.haut - hauteur >= MARGE) setHaut(position.haut - hauteur);
    else setHaut(MARGE);
  }, [position, children]);

  return (
    <div
      ref={bulle}
      className="kbulle"
      style={{
        left: position.x,
        top: haut ?? position.bas,
        visibility: haut === null ? 'hidden' : 'visible'
      }}
    >
      {children}
    </div>
  );
};

/**
 * Le survol d'un élément, et la bulle qui va avec — montée, positionnée et démontée.
 *
 * `HomePage` portait ce mécanisme à la main : un état, un calcul de position, un portail. Le
 * reprendre tel quel dans les dix endroits du dashboard Meta aurait fait dix copies d'un
 * placement à borner — et dix occasions de diverger sur la marge ou la largeur retenue.
 *
 * L'appelant pose `onMouseEnter={survoler(texte)}` et `onMouseLeave={quitter}` sur l'élément
 * **qu'il a déjà**, puis rend `{bulle}` n'importe où dans son arbre : elle sort par un portail,
 * sa place dans le JSX est donc sans effet. Un `texte` vide ne monte rien, ce qui permet de
 * câbler une colonne sans savoir si elle aura quelque chose à dire.
 *
 * ⚠️ Le texte est rendu en `pre-line` : les retours à la ligne de l'appelant sont conservés.
 * C'est ce qui permet d'énumérer des sources, une par ligne.
 */
const LARGEUR_MINIMALE = 340;
const MARGE_ECRAN = 12;

export const useBulleAuSurvol = () => {
  const [bulleCourante, setBulleCourante] = useState(null);

  const survoler = useCallback((texte) => (evenement) => {
    if (!texte) return;
    const zone = evenement.currentTarget.getBoundingClientRect();
    setBulleCourante({
      texte,
      // Bornée pour que la bulle tienne dans la fenêtre : les colonnes de droite sont
      // étroites, et la poser sur leur bord gauche la ferait sortir de l'écran.
      x: Math.max(MARGE_ECRAN, Math.min(zone.left, window.innerWidth - LARGEUR_MINIMALE - MARGE_ECRAN)),
      bas: zone.bottom + 6,
      haut: zone.top - 6
    });
  }, []);

  const quitter = useCallback(() => setBulleCourante(null), []);

  const bulle = bulleCourante
    ? ReactDOM.createPortal(
        <BulleSurvol position={bulleCourante}>
          <div style={{
            padding: '4px 6px',
            fontSize: '12px',
            color: '#4b5563',
            lineHeight: 1.5,
            whiteSpace: 'pre-line'
          }}>
            {bulleCourante.texte}
          </div>
        </BulleSurvol>,
        document.body
      )
    : null;

  return { survoler, quitter, bulle };
};

export default BulleSurvol;
